/* eslint-disable max-len */
import React, { useContext, useState, useEffect } from 'react';
import {
  oneOf,
  bool as boolType,
  any,
  shape as shapeType,
  string as stringType,
} from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  params
} from '@thd-nucleus/data-sources';
import {
  Col, Row, Image, Carousel
} from '@thd-olt-component-react/core-ui';
import classNames from 'classnames/bind';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import VisualTabs from './VisualTabs';
import CollectionsImage from './CollectionsImage';
import '../../styles/thd-recs-containers.scss';
import { FallbackCard } from './FallbackCard';
import styles from './RecommendationsProductPodCarousel.module.scss';
import { RecsProductPod } from './RecsProductPod';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const cx = classNames.bind(styles);
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string(),
  })
}, RecsProductPod);
const ProductPodV7Carousel = (props) => {

  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    data,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,
    showFallback,
    fallbackInfo,
    userId,
    subtitle,
    isCategoryTab, // Displaying the category tabs
    hideATC,
    hideFavorites,
    hideBadge,
    showSubscribeAndSave,
    membershipInformation,
    zipCode,
    slidesPer,
    keepAddToCart,
    hideSwatches,
    hideCarouselArrows,
    analyticsAnchorProductSku,
    parent,
    brandTitleMaxLine,
    hideRating
  } = props;

  let hideAddToCart;

  let recsCollectionTabCount = useConfigService('fs:recsCollectionTabCount') || 4;

  if (keepAddToCart === true) {
    hideAddToCart = false; // show ATC always - including MW
  } else if (hideATC || isMobile) {
    hideAddToCart = true;
  }

  const defaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null
  };

  const { products, metadata, categories } = data || {};

  let title = metadata?.collectionName || metadata?.title;

  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);

  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(filteredProducts);
  };

  let displayProducts;

  if (isCategorySelected) {
    displayProducts = filtered;
  } else {
    displayProducts = products;
  }

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6
      },
      xl: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6
      }
    });
  } else if (slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      md: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      lg: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer
      },
      xl: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer
      }
    });
  }

  let placeholderTxtLines;
  if (!isMobile && isCategoryTab) {
    placeholderTxtLines = 19;
  } else if (!isMobile && anchorImage) {
    placeholderTxtLines = 12;
  } else {
    placeholderTxtLines = 10;
  }

  return (
    <>
      {showLoading && loading && (
        showFallback && !userId && !products
          ? (
            <FallbackCard
              imageUrl={fallbackInfo.imageUrl}
              header={fallbackInfo.header}
              subheader={fallbackInfo.subheader}
              footerUrl={fallbackInfo.footerUrl}
              footer={fallbackInfo.footer}
            />
          )
          : <LoadingPlaceholder showMoreLines={(isCategoryTab)} txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />
      )}
      {displayProducts?.length > 0 && (
        <>
          {!hideTitle && (
            isCategoryTab && !metadata?.collectionName ? (
              <CarouselHeader title="More From This Brand" />
            ) : (
              <CarouselHeader title={title} subtitle={subtitle} />
            )
          )}
          {!isMobile && anchorImage && (
            <Col xs="4" className={cx('carouselAnchorImage')}>
              <Image
                src={anchorImage}
                alt="main-product-image"
                height="400"
                width="400"
                data-testid="anchor-image"
              />
              <div className={cx('carouselAnchorImage__currentItem__label')}>Current Item</div>
            </Col>
          )}
          {isCategoryTab && (
            <Row>
              <Col xs="4">
                {metadata?.collectionTitle1 ? (
                  <h3 className="pipCollection-title">{metadata?.collectionTitle1}</h3>
                ) : (
                  <h3 className="pipCollection-title">{products[0]?.product?.identifiers?.brandName}</h3>
                )}
                {metadata?.collectionTitle2 && (
                  <h3 className="pipCollection-subtitle">{metadata?.collectionTitle2}</h3>
                )}
              </Col>
              {!isMobile && (
                <Col xs={metadata?.collectionImageUrl || anchorImage || isCategoryTab ? '8' : '12'}>
                  {isCategoryTab && categories?.length > 0 && categories?.length >= recsCollectionTabCount && (
                    // eslint-disable-next-line max-len
                    <VisualTabs products={products} categories={categories} filterItemIds={pullFilteredData} />
                  )}
                </Col>
              )}
            </Row>
          )}
          {(() => {
            if (metadata?.collectionImageUrl || isCategoryTab) {
              return (
                <CollectionsImage metadata={metadata} firstProductMedia={products[0]?.product?.media} isMobile={isMobile} />
              );
            }
            return null;
          })()}
          <Col xs={anchorImage || isCategoryTab || metadata?.collectionImageUrl ? '8' : '12'}>
            <QueryProvider
              dataSource={scheme}
              cacheKey={`${scheme}-recommendations-carousel`}
              defaultVariables={defaultVariables}
            >
              <Carousel
                breakpoints={breakpoints}
                hideCarouselArrows={hideCarouselArrows}
              >
                {displayProducts.map((dynamicProduct, idx) => {
                  const { product } = dynamicProduct;
                  const { itemId } = product;
                  const { preferredPriceFlag } = product.pricing;

                  return (
                    <Wrapper key={idx}>
                      <RecsProductPod
                        itemId={itemId}
                        key={itemId}
                        position={idx}
                        storeId={storeId}
                        scheme={scheme}
                        anchorProduct={analyticsAnchorProductSku}
                        parent={parent}
                        hideSwatches={hideSwatches}
                        hideFavorites={hideFavorites}
                        hideBadge={hideBadge}
                        hideATC={hideATC}
                        hideRating={hideRating}
                        preferredPriceFlag={preferredPriceFlag}
                        brandTitleMaxLine={brandTitleMaxLine}
                      />
                    </Wrapper>
                  );
                }
                )}
              </Carousel>
            </QueryProvider>
          </Col>
        </>
      )}
    </>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, RecsProductPod.dataModel.product);

export const wrappedProduct1 = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});
ProductPodV7Carousel.dataModel = extend(Wrapper, wrappedProduct1);

ProductPodV7Carousel.displayName = 'ProductPodV7Carousel';

ProductPodV7Carousel.propTypes = {
  ...RecsProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  keepAddToCart: boolType,
  isCategoryTab: boolType,
  hideSwatches: boolType,
  hideCarouselArrows: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType
};

ProductPodV7Carousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,
  hideBadge: false,
  hideATC: false,
  hideFavorites: true,
  keepAddToCart: null,
  isCategoryTab: false,
  hideSwatches: true,
  hideCarouselArrows: false,
  analyticsAnchorProductSku: '',
  hideRating: false
};

export { ProductPodV7Carousel };
